import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import actions from "../Store/actions";

import {
    Grid,
    Box,
    InputLabel,
    MenuItem,
    FormControl,
    FormHelperText,
    TextField,
    Select,
} from "@mui/material/";
import makeStyles from "@mui/styles/makeStyles";
import LoadingButton from "@mui/lab/LoadingButton";
import { useToaster } from "../common/hooks";
import { ActionBar } from "../common/components";
import useCheckFormForErrors from "./useCheckFormForErrors";
import { validateEmail } from "./helper";
import { priority, classification } from "./data";
import styles from "./styles";
import { logToSentry } from "../config/Sentry";

const defaultForm = {
    contactName: "",
    emailAddress: "",
    description: "",
    subject: "",
    priority: 3,
    classification: 1,
};

const ContactForm = () => {
    const classes = makeStyles(styles)();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const toast = useToaster();

    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState({
        contactName: false,
        emailAddress: false,
        description: false,
        subject: false,
        priority: false,
        classification: false,
    });
    const [loading, setLoading] = useState(false);

    const onFieldChange = (fieldName) => (e) => {
        const { value } = e.target;

        let next = "";

        // email has a different validation, for the rest we check
        // if value is present and contains only valid characters
        if (fieldName === "emailAddress") {
            const isEmailVaid = validateEmail(value);
            setErrors({ ...errors, emailAddress: !isEmailVaid });
            next = value;
        } else {
            // allow only alpha-numeric and punctuation
            next = value.replace(/[^a-z0-9 - , ? . !]/gi, "");
            if (!next) setErrors({ ...errors, [fieldName]: true });
            else setErrors({ ...errors, [fieldName]: false });
        }

        setForm({ ...form, [fieldName]: next });
    };

    const onSelectionChange = (selectName) => (e) => {
        const { value } = e.target;

        if (!value) setErrors({ ...errors, [selectName]: true });
        else setErrors({ ...errors, [selectName]: false });

        setForm({ ...form, [selectName]: value });
    };

    const hdlSendClick = async () => {
        setLoading(true);
        const next = { ...form };
        next.priority = priority.find((item) => item.id == next.priority).name;
        next.classification = classification.find(
            (item) => item.id == next.classification
        ).name;

        try {
            await dispatch(actions.appSupport.create.postRequest(next));
            toast.info(t("messages.messageSentSuccess"), 2000);
        } catch (error) {
            console.error(error);
            toast.error(t("errors.emailPost"));
            logToSentry("hdlSendClick emailPost", { error });
        }

        setLoading(false);
        // reset form
        setForm(defaultForm);
    };

    // check that form does not have errors
    const hasErrors = useCheckFormForErrors(form, errors);

    return (
        <Box className={classes.container}>
            <FormControl margin="normal" fullWidth>
                <TextField
                    id="contact-name"
                    error={errors["contactName"]}
                    label={`${t("contactName")}*`}
                    onChange={onFieldChange("contactName")}
                    fullWidth
                    value={form.contactName}
                    placeholder={`${t("contactName")}*`}
                    helperText={
                        errors["contactName"] ? "Contact name is mandatory" : ""
                    }
                />
            </FormControl>
            <FormControl margin="normal" fullWidth>
                <TextField
                    id="email-address"
                    error={errors["emailAddress"]}
                    label={`${t("emailAddress")}*`}
                    onChange={onFieldChange("emailAddress")}
                    fullWidth
                    value={form.emailAddress}
                    placeholder={`${t("emailAddress")}*`}
                    helperText={errors["emailAddress"] ? "Email has error" : ""}
                />
            </FormControl>
            <FormControl margin="normal" fullWidth>
                <TextField
                    id="subject"
                    error={errors["subject"]}
                    label={`${t("subject")}*`}
                    onChange={onFieldChange("subject")}
                    fullWidth
                    value={form.subject}
                    placeholder={`${t("subject")}*`}
                    helperText={errors["subject"] ? "Subject is mandatory" : ""}
                />
            </FormControl>
            <FormControl margin="normal" fullWidth>
                <TextField
                    id="description"
                    error={errors["description"]}
                    label={`${t("description")}*`}
                    onChange={onFieldChange("description")}
                    fullWidth
                    multiline
                    rows={4}
                    value={form.description}
                    placeholder={`${t("description")}*`}
                    helperText={
                        errors["description"] ? "Description is mandatory" : ""
                    }
                />
            </FormControl>
            <FormControl margin="normal" fullWidth error={errors["priority"]}>
                <InputLabel id="priority-select-label">
                    {t("priority")}
                </InputLabel>
                <Select
                    labelId="priority-select-label"
                    id="priority-select"
                    value={form.priority}
                    label={t("priority")}
                    onChange={onSelectionChange("priority")}
                >
                    <MenuItem value={0}>
                        <em>None</em>
                    </MenuItem>
                    {priority.map((item, index) => (
                        <MenuItem
                            value={item.id}
                            key={index}
                            className={classes.menuItem}
                        >
                            {item.name}
                        </MenuItem>
                    ))}
                </Select>
                <FormHelperText>
                    {errors["priority"] ? "Priority is mandatory" : ""}
                </FormHelperText>
            </FormControl>
            <FormControl
                margin="normal"
                fullWidth
                error={errors["classification"]}
            >
                <InputLabel id="classification-select-label">
                    {t("classification")}
                </InputLabel>
                <Select
                    labelId="classification-select-label"
                    id="classification-select"
                    value={form.classification}
                    label={t("classification")}
                    onChange={onSelectionChange("classification")}
                >
                    <MenuItem value={0}>
                        <em>None</em>
                    </MenuItem>
                    {classification.map((item, index) => (
                        <MenuItem
                            value={item.id}
                            key={index}
                            className={classes.menuItem}
                        >
                            {item.name}
                        </MenuItem>
                    ))}
                </Select>
                <FormHelperText>
                    {errors["priority"] ? "Classification is mandatory" : ""}
                </FormHelperText>
            </FormControl>

            <ActionBar>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <LoadingButton
                            type="submit"
                            loading={loading}
                            variant="contained"
                            fullWidth
                            onClick={hdlSendClick}
                            disabled={hasErrors}
                            data-testid="send"
                        >
                            {t("send")}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </ActionBar>
        </Box>
    );
};

export default ContactForm;
